import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { RecognitionHistoryTab } from "@/pages/settings/recognitions/index/components/RecognitionHistoryTab.tsx";
import { RecognitionLibraryTab } from "@/pages/settings/recognitions/index/components/RecognitionLibraryTab.tsx";
import { useRecognitionHistoryFilters } from "@/pages/settings/recognitions/index/recognition-history.filters.tsx";
import { RecognitionSettingsTabs } from "@/pages/settings/recognitions/index/recognition-settings.types.ts";
import { RecognitionCategorySortModal } from "@/pages/settings/recognitions/widgets/RecognitionCategorySortModal.tsx";
import { RecognitionEditModal } from "@/pages/settings/recognitions/widgets/RecognitionEditModal.tsx";
import { useRecognitionCategories } from "@/queries/use-recognitions.query.ts";
import { ApiRecognitionHistoryFilter } from "@/types/recognitions/recognition.types.ts";
import { Button, FilterButton, Page, Stack, TabItem, Tabs, useModals } from "@skailer-com/uikit";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

export interface Props {
  tab: RecognitionSettingsTabs;
}

export function RecognitionCategoriesPage({ tab }: Props) {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const navigate = useNavigate();

  const { data: categories, isLoading } = useRecognitionCategories();

  useTitle(t("recognition:settings_title"), t("common:settings"));

  const isHistoryTab = tab === RecognitionSettingsTabs.history;

  const [searchParams] = useSearchParams();
  const isPeriodActive = searchParams.has("date_from") || searchParams.has("date_to");

  const { filterButton } = useAppFilter<ApiRecognitionHistoryFilter>({
    filters: useRecognitionHistoryFilters(),
    source: "Recognition history",
    renderFilterButton: ({ onClick, onClear, count }) => (
      <FilterButton
        count={count + (isPeriodActive ? 1 : 0)}
        onClick={onClick}
        onClear={onClear}
        title={t("common:filter")}
      />
    ),
  });

  const handleTabChanged = (tabIndex: number) => {
    const urls = ["/settings/recognitions", "/settings/recognitions/history"];
    navigate(urls[tabIndex]);
  };

  return (
    <Page className={isHistoryTab ? "h-full" : undefined}>
      <Page.Header title={t("recognition:settings_title")} showBack={true} navigateTo={"/settings"}>
        {isHistoryTab ? (
          filterButton({ clearable: true })
        ) : (
          <Stack direction={"horizontal"} gap={"sm"}>
            {(categories?.length ?? 0) > 1 && (
              <Button variant={"secondary"} onClick={() => openModal(RecognitionCategorySortModal)}>
                {t("common:reorder")}
              </Button>
            )}
            <Button onClick={() => openModal(RecognitionEditModal, {})}>
              {t("recognition:recognition_create_title")}
            </Button>
          </Stack>
        )}
      </Page.Header>
      <Page.Content stretch={isHistoryTab}>
        <Stack className={isHistoryTab ? "px-16 flex-1 min-h-0 min-w-0" : "px-16"}>
          <Tabs
            fill={isHistoryTab}
            onActiveTabChange={handleTabChanged}
            activeTab={isHistoryTab ? 1 : 0}
          >
            <TabItem title={t("common:library")}>
              <RecognitionLibraryTab categories={categories} isLoading={isLoading} />
            </TabItem>
            <TabItem title={t("common:history")} className={"flex min-h-0 min-w-0 flex-1 flex-col"}>
              <RecognitionHistoryTab />
            </TabItem>
          </Tabs>
        </Stack>
      </Page.Content>
    </Page>
  );
}
